/**
 * aawl/hero-slider front-end init.
 *
 * Plan 015 Phase 2 + Phase 5b (issue #31). The nav rework retired Swiper's
 * Pagination module entirely — we render our own paw-shaped nav in render.php
 * (4 toe-buttons + prev/next arrows). Swiper's Navigation module wires the
 * arrows; we wire the toe-buttons + active-state sync ourselves.
 *
 * Init contract:
 *
 *   - Find every .c-hero-slider[data-swiper] element on the page
 *   - Parse data-swiper as JSON for per-instance config
 *   - Boot Swiper with: navigation (chevron arrows) + a11y + autoplay
 *     (when configured AND prefers-reduced-motion is not set)
 *   - Wire toe-button click → swiper.slideToLoop(toeIndex)
 *   - Sync .is-active on the corresponding toe path on slideChange
 *   - One slider per element; multi-instance safe
 *
 * Dropped vs prior implementation:
 *   - Pagination module + renderBullet (we render in PHP, not JS)
 *   - stripPaginationAriaHidden lifecycle hook (#30 workaround retired —
 *     custom DOM has correct semantics from boot, axe-clean without scrubbing)
 *   - swiper/css/pagination import (no Swiper-rendered bullets to style)
 */

import { Swiper } from 'swiper';
import { Navigation, Autoplay, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/a11y';

const prefersReducedMotion = () =>
	window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Sync the .is-active class on the paw's toe paths to the current slide.
 * Uses Swiper's realIndex (loop-aware) so the highlight tracks the *visible*
 * slide, not the cloned-for-loop slide index.
 */
const syncActiveToe = ( swiperInstance, navEl ) => {
	if ( ! navEl ) return;
	const activeIndex = ( swiperInstance.realIndex ?? swiperInstance.activeIndex ) + 1;
	navEl.querySelectorAll( '.c-slider-nav__pad--toe' ).forEach( ( pad ) => {
		const isActive = String( pad.dataset.toe ) === String( activeIndex );
		pad.classList.toggle( 'is-active', isActive );
	} );
	navEl.querySelectorAll( '.c-slider-nav__toe-btn' ).forEach( ( btn ) => {
		const isActive = String( btn.dataset.toe ) === String( activeIndex );
		btn.setAttribute( 'aria-current', isActive ? 'true' : 'false' );
	} );
};

export function initHeroSliders() {
	const sliders = document.querySelectorAll( '.c-hero-slider[data-swiper]' );
	if ( ! sliders.length ) return;

	sliders.forEach( ( el ) => {
		let userConfig = {};
		try {
			userConfig = JSON.parse( el.dataset.swiper || '{}' );
		} catch ( err ) {
			// eslint-disable-next-line no-console
			console.warn( 'aawl/hero-slider: malformed data-swiper, using defaults', err );
		}

		const navEl    = el.querySelector( '.c-slider-nav' );
		const prevEl   = el.querySelector( '.c-slider-nav__arrow--prev' );
		const nextEl   = el.querySelector( '.c-slider-nav__arrow--next' );
		const toeBtns  = el.querySelectorAll( '.c-slider-nav__toe-btn' );

		const config = {
			modules: [ Navigation, Autoplay, A11y ],
			loop: !! userConfig.loop,
			speed: userConfig.speed || 600,
			navigation: {
				prevEl,
				nextEl,
			},
			a11y: {
				prevSlideMessage: 'Previous slide',
				nextSlideMessage: 'Next slide',
			},
			on: {
				init: ( swiperInstance ) => syncActiveToe( swiperInstance, navEl ),
				slideChange: ( swiperInstance ) => syncActiveToe( swiperInstance, navEl ),
			},
		};

		if ( userConfig.autoplay && ! prefersReducedMotion() ) {
			config.autoplay = {
				delay: ( userConfig.autoplay.delay ) || 6000,
				pauseOnMouseEnter: true,
				disableOnInteraction: false,
			};
		}

		const swiper = new Swiper( el, config );

		// Wire toe-button clicks. data-toe is 1-indexed (matches design + a11y
		// labels); slideToLoop is 0-indexed, so subtract 1.
		toeBtns.forEach( ( btn ) => {
			btn.addEventListener( 'click', () => {
				const toeIndex = parseInt( btn.dataset.toe, 10 );
				if ( Number.isNaN( toeIndex ) ) return;
				if ( typeof swiper.slideToLoop === 'function' ) {
					swiper.slideToLoop( toeIndex - 1 );
				} else {
					swiper.slideTo( toeIndex - 1 );
				}
			} );
		} );

		// Wire the heel-pad click → advance one slide (BugHerd #133 — "clicking
		// the paw" rotates the hero to the next slide). The toes jump to a
		// specific slide; the heel is the "next" affordance.
		const heelBtn = el.querySelector( '.c-slider-nav__heel-btn' );
		if ( heelBtn ) {
			heelBtn.addEventListener( 'click', () => {
				if ( typeof swiper.slideNext === 'function' ) {
					swiper.slideNext();
				}
			} );
		}
	} );
}
